import { useEffect, useState } from 'react'
import {
  useIsInvalidPlayer,
  usePlayerActions
} from 'src/store/player'
import * as actions from 'src/actions'
import { stripQueryParams } from 'src/utils/stripQueryParams'
import { getValidDecodedToken } from 'src/utils/getValidDecodedToken'
import type { PlayerDTO } from 'src/actions/types'

export const useOneTimeLogin = (otl?: string) => {
  const playerActions = usePlayerActions()
  const isInvalidPlayer = useIsInvalidPlayer()
  const [token, setToken] = useState<string>()

  useEffect(() => {
    const fetchToken = async (otlCode: string): Promise<void> => {
      try {
        const tokenResponse = await actions.exchangeToken({ otl: otlCode })

        if (tokenResponse.error) {
          if (tokenResponse.error !== 'OneTimeLoginAlreadyUsedError') {
            console.error('Exchanging token failed:', tokenResponse.error)
          }

          playerActions.notAuthenticated()
          return
        }

        const accessToken = tokenResponse.data?.accessToken
        setToken(accessToken)

        stripQueryParams(['otl', 'country', 'language'])
      } catch (error) {
        console.error('Error exchanging token:', error)
      }
    }

    if (otl) void fetchToken(otl)
  }, [otl, playerActions])

  useEffect(() => {
    // If the player is authenticated, we don't need to fetch the player
    // If one time login code is provided, but the token is not set, we don't need to fetch the player
    if (!isInvalidPlayer || (otl && !token)) return

    const initializePlayer = async () => {
      try {
        let player: PlayerDTO | undefined

        if (token) {
          const decoded = getValidDecodedToken(token)
          if (!decoded) {
            playerActions.notAuthenticated()
            return
          }

          player = { id: decoded.externalId, token }
        } else {
          // fallback to session token
          player = await actions.getPlayerFromToken()
          if (!player) {
            playerActions.notAuthenticated()
            return
          }
        }

        playerActions.initialized({
          type: 'InitializedPlayer',
          id: player.id,
          token: player.token
        })
      } catch (error) {
        console.error('Error initializing player:', error)
        playerActions.notAuthenticated()
      }
    }

    void initializePlayer()
  }, [token, otl, playerActions, isInvalidPlayer])
}
